import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RequireAuth from './components/RequireAuth';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptCustomerInvite from './pages/AcceptCustomerInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CustomerDashboard from './pages/customer/Dashboard';
import CustomerQuotes from './pages/customer/Quotes';
import CustomerOrders from './pages/customer/Orders';
import CustomerInvoices from './pages/customer/Invoices';
import CustomerPayments from './pages/customer/Payments';
import CustomerPaymentMethods from './pages/customer/PaymentMethods';
import CustomerProfile from './pages/customer/Profile';
import DriverDashboard from './pages/driver/Dashboard';
import DriverJobs from './pages/driver/Jobs';
import DriverMap from './pages/driver/Map';
import DriverDepots from './pages/driver/Depots';
import DriverProfile from './pages/driver/Profile';
import DriverReports from './pages/driver/Reports';
import DriverSchedule from './pages/driver/Schedule';
import ManagerDashboard from './pages/manager/Dashboard';
import ManagerDrivers from './pages/manager/Drivers';
import ManagerJobs from './pages/manager/Jobs';
import ManagerInvoices from './pages/manager/Invoices';
import ManagerVehicles from './pages/manager/Vehicles';
import ManagerCustomers from './pages/manager/Customers';
import ManagerMap from './pages/manager/Map';
import ManagerDepots from './pages/manager/Depots';
import ManagerProfile from './pages/manager/Profile';
import ManagerReports from './pages/manager/Reports';
import ManagerOrganization from './pages/manager/Organization';
import ManagerPricing from './pages/manager/Pricing';
import PayInvoice from './pages/public/PayInvoice';
import SuperuserRoute from './components/SuperuserRoute';
import UserAdmin from './pages/admin/UserAdmin';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1E3A5F', // Dark blue from logo
      light: '#4A90E2', // Lighter blue from logo
      dark: '#152a4f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4A90E2', // Lighter blue from logo
      light: '#6BA3E8',
      dark: '#3A7BC2',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1E3A5F',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: '#1E3A5F',
    },
    h5: {
      fontWeight: 600,
      color: '#1E3A5F',
    },
    h6: {
      fontWeight: 600,
      color: '#1E3A5F',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            minHeight: 44,
          },
        }),
        contained: {
          backgroundColor: '#1E3A5F',
          color: '#ffffff',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#2A4A6F',
          },
        },
        outlined: {
          borderColor: '#1E3A5F',
          color: '#1E3A5F',
          '&:hover': {
            borderColor: '#1E3A5F',
            backgroundColor: 'rgba(30, 58, 95, 0.08)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            padding: 12,
          },
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            minHeight: 48,
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            margin: 0,
            maxHeight: '100%',
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#1E3A5F',
          color: '#ffffff',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#1E3A5F',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: '#666666',
          fontWeight: 500,
          textTransform: 'none',
          minHeight: 48,
          [theme.breakpoints.down('md')]: {
            minWidth: 'auto',
            px: 1.5,
            fontSize: '0.8125rem',
          },
          '&.Mui-selected': {
            color: '#1E3A5F',
            fontWeight: 600,
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/accept-customer-invite"
              element={
                <RequireAuth>
                  <AcceptCustomerInvite />
                </RequireAuth>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
            <Route path="/reset-password/:uid/:token/" element={<ResetPassword />} />
            <Route path="/pay" element={<PayInvoice />} />

            <Route
              path="/admin/users"
              element={
                <SuperuserRoute>
                  <UserAdmin />
                </SuperuserRoute>
              }
            />

            {/* Customer Routes */}
            <Route
              path="/customer/dashboard"
              element={<ProtectedRoute role="customer"><CustomerDashboard /></ProtectedRoute>}
            />
            <Route
              path="/customer/quotes"
              element={<ProtectedRoute role="customer"><CustomerQuotes /></ProtectedRoute>}
            />
            <Route
              path="/customer/orders"
              element={<ProtectedRoute role="customer"><CustomerOrders /></ProtectedRoute>}
            />
            <Route
              path="/customer/invoices"
              element={<ProtectedRoute role="customer"><CustomerInvoices /></ProtectedRoute>}
            />
            <Route
              path="/customer/payments"
              element={<ProtectedRoute role="customer"><CustomerPayments /></ProtectedRoute>}
            />
            <Route
              path="/customer/payment-methods"
              element={<ProtectedRoute role="customer"><CustomerPaymentMethods /></ProtectedRoute>}
            />
            <Route
              path="/customer/profile"
              element={<ProtectedRoute role="customer"><CustomerProfile /></ProtectedRoute>}
            />
            
            {/* Driver Routes */}
            <Route
              path="/driver/dashboard"
              element={<ProtectedRoute role="driver"><DriverDashboard /></ProtectedRoute>}
            />
            <Route
              path="/driver/jobs"
              element={<ProtectedRoute role="driver"><DriverJobs /></ProtectedRoute>}
            />
            <Route
              path="/driver/map"
              element={<ProtectedRoute role="driver"><DriverMap /></ProtectedRoute>}
            />
            <Route
              path="/driver/depots"
              element={<ProtectedRoute role="driver"><DriverDepots /></ProtectedRoute>}
            />
            <Route
              path="/driver/schedule"
              element={<ProtectedRoute role="driver"><DriverSchedule /></ProtectedRoute>}
            />
            <Route
              path="/driver/reports"
              element={<ProtectedRoute role="driver"><DriverReports /></ProtectedRoute>}
            />
            <Route
              path="/driver/profile"
              element={<ProtectedRoute role="driver"><DriverProfile /></ProtectedRoute>}
            />
            
            {/* Manager Routes */}
            <Route
              path="/manager/dashboard"
              element={<ProtectedRoute role="manager"><ManagerDashboard /></ProtectedRoute>}
            />
            <Route
              path="/manager/drivers"
              element={<ProtectedRoute role="manager"><ManagerDrivers /></ProtectedRoute>}
            />
            <Route
              path="/manager/jobs"
              element={<ProtectedRoute role="manager"><ManagerJobs /></ProtectedRoute>}
            />
            <Route
              path="/manager/invoices"
              element={<ProtectedRoute role="manager"><ManagerInvoices /></ProtectedRoute>}
            />
            <Route
              path="/manager/vehicles"
              element={<ProtectedRoute role="manager"><ManagerVehicles /></ProtectedRoute>}
            />
            <Route
              path="/manager/customers"
              element={<ProtectedRoute role="manager"><ManagerCustomers /></ProtectedRoute>}
            />
            <Route
              path="/manager/map"
              element={<ProtectedRoute role="manager"><ManagerMap /></ProtectedRoute>}
            />
            <Route
              path="/manager/depots"
              element={<ProtectedRoute role="manager"><ManagerDepots /></ProtectedRoute>}
            />
            <Route
              path="/manager/reports"
              element={<ProtectedRoute role="manager"><ManagerReports /></ProtectedRoute>}
            />
            <Route
              path="/manager/profile"
              element={<ProtectedRoute role="manager"><ManagerProfile /></ProtectedRoute>}
            />
            <Route
              path="/manager/organization"
              element={<ProtectedRoute role="manager"><ManagerOrganization /></ProtectedRoute>}
            />
            <Route
              path="/manager/pricing"
              element={<ProtectedRoute role="manager"><ManagerPricing /></ProtectedRoute>}
            />
            
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;



